import { Link } from 'react-router-dom';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExitToAppRounded as ExitToAppIcon } from '@material-ui/icons';

import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { formatShortDate } from '../../../shared/utils/format-date.util';
import { formatPhoneNumber } from '../../../shared/utils/format-phone-number.util';
import { ADMIN_USERS_APP_PATHS } from '../../constants/users-paths.constants';
import { UserListItem, UserSortColumn, UserSortDirection } from '../../types/user.type';
import { getMainAddress } from '../../utils/get-main-address.util';
import { UserRoleChip } from '../Chips/UserRole.chip';

interface UsersTableHeader {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
  sortColumn?: UserSortColumn;
}

interface UsersTableProps {
  users: UserListItem[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  sortBy: UserSortColumn;
  sortDirection: UserSortDirection;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSortChange: (column: UserSortColumn) => void;
}

const TABLE_HEADERS: UsersTableHeader[] = [
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 50, align: 'center' },
  { key: 'role', label: 'Rôle', minWidth: 150, align: 'left', sortColumn: 'role' },
  { key: 'name', label: 'Nom', minWidth: 200, align: 'left', sortColumn: 'lastname' },
  { key: 'address', label: 'Adresse principale', minWidth: 250, align: 'left' },
  { key: 'contact', label: 'Contact', minWidth: 220, align: 'left', sortColumn: 'email' },
  { key: 'status', label: 'Statut', minWidth: 100, align: 'center' },
  { key: 'createdAt', label: 'Créé le', minWidth: 110, align: 'left', sortColumn: 'createdAt' },
];

const COLUMN_COUNT = TABLE_HEADERS.length;

const useStyles = makeStyles({
  secondaryLine: {
    display: 'block',
    fontSize: 12,
    color: '#757575',
  },
});

function UsersTableHeaderCell({
  header,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  header: UsersTableHeader;
  sortBy: UserSortColumn;
  sortDirection: UserSortDirection;
  onSortChange: (column: UserSortColumn) => void;
}) {
  const { sortColumn } = header;
  const isSorted = sortColumn === sortBy;

  return (
    <TableCell
      aria-label={header.ariaLabel}
      align={header.align}
      sortDirection={isSorted ? sortDirection : false}
      style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
    >
      {sortColumn ? (
        <TableSortLabel
          active={isSorted}
          direction={isSorted ? sortDirection : 'asc'}
          onClick={() => onSortChange(sortColumn)}
        >
          {header.label}
        </TableSortLabel>
      ) : (
        header.label
      )}
    </TableCell>
  );
}

function UserRow({ user }: { user: UserListItem }) {
  const classes = useStyles();
  const mainAddress = getMainAddress(user.addresses);

  return (
    <TableRow hover tabIndex={-1}>
      <TableCell align="center">
        <IconButton size="small" aria-label="voir" component={Link} to={ADMIN_USERS_APP_PATHS.USER_DETAILS(user.id)}>
          <ExitToAppIcon />
        </IconButton>
      </TableCell>
      <TableCell align="left">
        <UserRoleChip role={user.role} />
      </TableCell>
      <TableCell align="left">
        {user.firstname} {user.lastname}
      </TableCell>
      <TableCell align="left">
        {mainAddress ? (
          <>
            {mainAddress.street}
            <span className={classes.secondaryLine}>
              {mainAddress.zipCode} {mainAddress.city}
            </span>
          </>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell align="left">
        {user.email || '-'}
        <span className={classes.secondaryLine}>{formatPhoneNumber(user.phoneNumber) || '-'}</span>
      </TableCell>
      <TableCell align="center">
        <StatusChip isActive={user.isActive} inactiveLabel="Désactivé" />
      </TableCell>
      <TableCell align="left">{formatShortDate(user.createdAt)}</TableCell>
    </TableRow>
  );
}

export function UsersTable({
  users,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  sortBy,
  sortDirection,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: UsersTableProps) {
  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <UsersTableHeaderCell
                  key={header.key}
                  header={header}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSortChange={onSortChange}
                />
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={COLUMN_COUNT} />}
          {!isLoading && (
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} style={{ textAlign: 'center' }}>
                    Aucun utilisateur
                  </TableCell>
                </TableRow>
              )}
              {users.map((user) => (
                <UserRow key={user.id} user={user} />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}
